/**
 * Texto do Resumo executivo — montado por template a partir dos números do
 * relatório, sem IA, para ser reproduzível e auditável: o mesmo dado sempre
 * gera a mesma frase.
 */

import { fmtArea, fmtEnergy, fmtPower } from "../../../utils/formatUnits";
import type { EnergyType, ReportSubject } from "./types";

export const ENERGY_LABEL: Record<EnergyType, string> = {
  solar: "solar fotovoltaica",
  eolico: "eólica",
};

const pct = (value: number) =>
  `${value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;

const km = (value: number) =>
  `${value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} km`;

function subjectIntro(subject: ReportSubject): string {
  const area = subject.areaKm2 != null ? fmtArea(subject.areaKm2) : null;
  switch (subject.kind) {
    case "estado":
      return `Pernambuco continental tem ${area ?? "área indisponível"}`;
    case "municipio":
      return `O município de ${subject.name} tem ${area ?? "área indisponível"}`;
    default: {
      const where = subject.municipalityName
        ? `, majoritariamente em ${subject.municipalityName},`
        : "";
      return `A área "${subject.name}"${where} tem ${area ?? "área indisponível"}`;
    }
  }
}

function energySentence(
  subject: ReportSubject,
  energy: EnergyType,
  levelLabel: string,
  first: boolean
): string | null {
  const stats = subject.analyses[energy]?.detailedLevel.stats;
  if (!stats) return null;
  const lead = first ? `${subjectIntro(subject)}; ` : "Para a geração ";
  const body = first
    ? `${pct(stats.percentage)} dela são propícios à geração ${ENERGY_LABEL[energy]} no nível ${levelLabel}`
    : `${ENERGY_LABEL[energy]}, ${pct(stats.percentage)} da área são propícios no mesmo nível`;
  return (
    `${lead}${body}, o que corresponde a um potencial instalável de ` +
    `${fmtPower(stats.installable_gw)} e a uma geração estimada de ` +
    `${fmtEnergy(stats.annual_energy_gwh)} por ano.`
  );
}

function infrastructureSentence(subject: ReportSubject): string | null {
  const find = (id: string) =>
    subject.infrastructure.find((d) => d.id === id)?.distanceKm ?? null;
  const substation = find("subestacao");
  const line = find("linha_transmissao");
  if (substation == null && line == null) return null;
  const parts: string[] = [];
  if (substation != null) parts.push(`a subestação mais próxima está a ${km(substation)}`);
  if (line != null) parts.push(`a linha de transmissão mais próxima, a ${km(line)}`);
  const sentence = parts.join("; ");
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
}

function vocacaoSentence(subject: ReportSubject): string | null {
  const vocacao = subject.vocacaoPrincipal;
  if (!vocacao || !subject.municipalityName) return null;
  const maturidade = vocacao.maturidade ? ` (${vocacao.maturidade.toLowerCase()})` : "";
  return `A vocação principal de ${subject.municipalityName}, segundo a ADEPE, é ${vocacao.nome}${maturidade}.`;
}

export function executiveSummaryText(
  subject: ReportSubject,
  energies: EnergyType[],
  levelLabel: string
): string {
  const sentences: string[] = [];
  energies.forEach((energy) => {
    const sentence = energySentence(subject, energy, levelLabel, sentences.length === 0);
    if (sentence) sentences.push(sentence);
  });
  if (sentences.length === 0) {
    sentences.push(
      `${subjectIntro(subject)}. Os números de potencial não puderam ser calculados neste momento.`
    );
  }
  const infrastructure = infrastructureSentence(subject);
  if (infrastructure) sentences.push(infrastructure);
  const vocacao = vocacaoSentence(subject);
  if (vocacao) sentences.push(vocacao);
  return sentences.join(" ");
}
